import { useSelector } from "@legendapp/state/react"
import { syncStore$ } from "@/stores/sync.v2.store"
import { syncToPowerDialerListId, hasPowerDialerSelection } from "@/lib/powerDialerUtils"
import { getRememberedPowerDialerSelectionType, rememberPowerDialerSelectionType } from "@/features/PowerDialer/selection/selectionTypeMemory"
import { useEffect, useMemo } from "react"
import { useHasHydrated } from "@/hooks/useHasHydrated"
import { useAppStoreContext } from "@/stores/appStore"
import { toPositiveNumber, type PowerDialerPreferenceSnapshot } from "@/features/PowerDialer/powerDialerSnapshot"

function normalizeSelectionType(value: unknown) {
  return value === "personal" || value === "team" || value === "sequence" ? value : null
}

/**
 * Custom hook to access PowerDialer selection state from syncStore
 * Provides both raw syncStore values and computed values for backwards compatibility
 */
export function usePowerDialerSelection() {
  const hasHydrated = useHasHydrated()
  const userData = useAppStoreContext((state) => state.userData)
  const snapshotPreferences = userData.preferences as PowerDialerPreferenceSnapshot
  const powerDialerTargetTypeFromSync = useSelector(() => syncStore$.userPreferences.powerDialerTargetType.get())
  const powerDialerTargetIdFromSync = useSelector(() => syncStore$.userPreferences.powerDialerTargetId.get())
  const personalListSequenceIdFromSync = useSelector(() => syncStore$.userPreferences.personalListSequenceId.get())
  const todoListSequenceIdFromSync = useSelector(() => syncStore$.userPreferences.todoListSequenceId.get())

  const powerDialerTargetId = hasHydrated ? powerDialerTargetIdFromSync : toPositiveNumber(snapshotPreferences.powerDialerTargetId)
  const personalListSequenceId = hasHydrated ? personalListSequenceIdFromSync : toPositiveNumber(snapshotPreferences.personalListSequenceId)
  const todoListSequenceId = hasHydrated ? todoListSequenceIdFromSync : toPositiveNumber(snapshotPreferences.todoListSequenceId)
  const powerDialerTargetTypeFromSnapshot = normalizeSelectionType(snapshotPreferences.powerDialerTargetType)
  const powerDialerTargetTypeSource = hasHydrated ? powerDialerTargetTypeFromSync : powerDialerTargetTypeFromSnapshot

  const powerDialerTargetType = useMemo(() => {
    if (powerDialerTargetTypeSource === "personal" || powerDialerTargetTypeSource === "team" || powerDialerTargetTypeSource === "sequence") {
      return powerDialerTargetTypeSource
    }

    if (!powerDialerTargetId || powerDialerTargetId <= 0) {
      return null
    }

    if (powerDialerTargetId === personalListSequenceId || powerDialerTargetId === todoListSequenceId) {
      return "personal"
    }

    if (!hasHydrated) {
      return "team"
    }

    return getRememberedPowerDialerSelectionType(powerDialerTargetId) ?? "team"
  }, [powerDialerTargetTypeSource, powerDialerTargetId, personalListSequenceId, todoListSequenceId, hasHydrated])

  useEffect(() => {
    if (!powerDialerTargetId || powerDialerTargetId <= 0) {
      return
    }

    if (powerDialerTargetType !== "personal" && powerDialerTargetType !== "team" && powerDialerTargetType !== "sequence") {
      return
    }

    rememberPowerDialerSelectionType({
      targetId: powerDialerTargetId,
      targetType: powerDialerTargetType,
    })
  }, [powerDialerTargetId, powerDialerTargetType])

  return {
    // Raw syncStore values (source of truth)
    powerDialerTargetType,
    powerDialerTargetId,

    // Computed values for backwards compatibility
    listId: syncToPowerDialerListId(powerDialerTargetType, powerDialerTargetId),
    hasSelection: hasPowerDialerSelection(powerDialerTargetType, powerDialerTargetId),
  }
}
